import { useEffect, useRef, useState, type JSX } from 'react';

import { Button, Icon } from '@bookora/ui';

import { UNDO } from './copy';
import './guide.css';

/** How long «بازگرداندن» stays on offer. */
export const UNDO_VISIBLE_MS = 6_000;
/** The collapse, matched to `--bk-duration-fast` (the reduced-motion clamp shortens it). */
const COLLAPSE_MS = 150;

/**
 * The in-place undo after a tip is snoozed, closed, set aside or marked done.
 *
 * It takes the tip's own slot rather than a toast — the user is looking at the
 * place the tip was, and a toast in a corner answers a question they did not
 * ask. A polite `role="status"` announces the sentence; «بازگرداندن» is a real
 * button. After six seconds the row collapses and the slot closes up. The
 * server keeps an exact snapshot for thirty seconds, so an undo puts back the
 * counters as well as the tip (two «×, then undo» never add up to a back-off).
 */
export function GuideUndoRow({
  message,
  onRestore,
  onExpire,
}: {
  message: string;
  onRestore: () => void;
  onExpire: () => void;
}): JSX.Element {
  const [leaving, setLeaving] = useState(false);
  const expire = useRef(onExpire);
  useEffect(() => {
    expire.current = onExpire;
  });

  useEffect(() => {
    let collapse: ReturnType<typeof setTimeout> | undefined;
    const timer = setTimeout(() => {
      setLeaving(true);
      collapse = setTimeout(() => expire.current(), COLLAPSE_MS);
    }, UNDO_VISIBLE_MS);
    return () => {
      clearTimeout(timer);
      clearTimeout(collapse);
    };
  }, []);

  return (
    <div
      className={['bk-guide-undo', leaving ? 'is-leaving' : null].filter(Boolean).join(' ')}
      role="status"
    >
      <span className="bk-guide-undo__text">
        <Icon name="check" size={16} />
        {message}
      </span>
      <Button size="small" type="link" onClick={onRestore}>
        {UNDO.restore}
      </Button>
    </div>
  );
}
